import React, { useEffect } from "react";
import QuestionWidget from "./QuestionWidget";
import DataCollectionWidget from "./DataCollectionWidget";
import SlotPickerWidget from "./SlotPickerWidget";
import ConfirmationWidget from "./ConfirmationWidget";
import SummaryCard from "./SummaryCard";
import ProgressTracker from "./ProgressTracker";
import { WidgetCollapsed, WidgetErrorBoundary, WidgetFallback } from "./WidgetStates";
import { fieldComponent } from "./fields/FieldControl";
import { allFields } from "./useWidgetForm";
import { NATIVE_PAYLOAD_VERSION, NATIVE_TIER, WIDGET_STATE } from "./kinds";
import { useWidgetSubmit } from "../../../hooks/widget/useWidgetSubmit";
import { reportWidgetEvent } from "../../../utils/widgetTelemetry";

/** One component per kind in `RENDERED_WIDGET_KINDS` (the test proves the two lists agree). */
export const WIDGET_COMPONENTS = Object.freeze({
	question: QuestionWidget,
	data_collection: DataCollectionWidget,
	slot_picker: SlotPickerWidget,
	confirmation: ConfirmationWidget,
	summary: SummaryCard,
	progress: ProgressTracker,
});

/** Display-only kinds never collapse: they have nothing to submit, whatever `state` says. */
const DISPLAY_ONLY = new Set(["summary", "progress"]);

const isPlainObject = (v) => v !== null && typeof v === "object" && !Array.isArray(v);

/**
 * Why an envelope cannot be rendered natively, or null when it can (D-W10). Tier first, then
 * `payloadVersion`, then the kind, then (data_collection) every field type.
 */
export function fallbackReason(envelope) {
	if (!isPlainObject(envelope) || envelope.tier !== NATIVE_TIER) return "unknown_tier";
	if (envelope.payloadVersion !== NATIVE_PAYLOAD_VERSION) return "unknown_version";
	if (!WIDGET_COMPONENTS[envelope.kind] || !isPlainObject(envelope.payload)) return "unknown_kind";
	if (envelope.kind === "data_collection" && allFields(envelope.payload).some((f) => !fieldComponent(f))) return "unknown_field_type";
	return null;
}

const safeId = (recordId) => `w-${String(recordId ?? "x").replace(/[^A-Za-z0-9_-]/g, "")}`;

/**
 * The Tier-1 dispatch (spec-widget-mcp 05 Step 2). Unknown tier / version / kind / field type →
 * `fallbackText` (never a blank); a submitted or expired interactive widget → its collapsed summary;
 * otherwise the kind's component. `send` is Chat's `handleRequest` (the submit transport).
 */
export default function WidgetRenderer({ envelope, recordId, conversationId, send, now }) {
	const kind = isPlainObject(envelope) ? envelope.kind : undefined;
	const submitter = useWidgetSubmit({ recordId, kind, conversationId, send });
	const reason = fallbackReason(envelope);
	const shown = !reason && envelope.state !== WIDGET_STATE.SUBMITTED && envelope.state !== WIDGET_STATE.EXPIRED;

	useEffect(() => {
		if (shown) reportWidgetEvent("widget_shown_v1", { tier: NATIVE_TIER, kind, host: "widget" }, { once: true, onceKey: String(recordId) });
	}, [shown, kind, recordId]);

	if (reason) return <WidgetFallback envelope={envelope} reason={reason} recordId={recordId} />;

	if (!DISPLAY_ONLY.has(kind)) {
		const submitted = envelope.state === WIDGET_STATE.SUBMITTED || submitter.collapse === WIDGET_STATE.SUBMITTED;
		const expired = envelope.state === WIDGET_STATE.EXPIRED || submitter.collapse === WIDGET_STATE.EXPIRED;
		if (submitted || expired) {
			return (
				<WidgetCollapsed
					envelope={envelope}
					state={submitted ? WIDGET_STATE.SUBMITTED : WIDGET_STATE.EXPIRED}
					values={envelope.submittedValues || submitter.sentValues}
				/>
			);
		}
	}

	const Component = WIDGET_COMPONENTS[kind];
	return (
		<WidgetErrorBoundary envelope={envelope} recordId={recordId}>
			<Component envelope={envelope} idPrefix={safeId(recordId)} submitter={submitter} now={now} />
		</WidgetErrorBoundary>
	);
}
